import type { SceneShotCue } from "@heroncendre/schema";

/**
 * ============================================================================
 *  segments.ts — Les scènes d'un chapitre, découpées dans sa piste
 * ----------------------------------------------------------------------------
 *  Un chapitre a UNE piste audio et une séquence de plans (`sceneShot`) : chaque
 *  plan est une scène, qui dure de son instant jusqu'à celui du plan suivant. Ce
 *  sont ces segments que dessine la barre du bas (largeur proportionnelle à la
 *  durée) et que parcourent ⏮ / ⏭. Le document n'a pas de « durée par scène » :
 *  elle se déduit de la séquence, et reste donc juste quand la voix est refaite
 *  (`pnpm voice` recale les plans).
 *
 *  Fonctions pures : l'environnement de test du dépôt (`node`) les éprouve.
 * ==========================================================================*/

export interface Segment {
  index: number;
  sceneId: string;
  /** Secondes dans la piste. Le premier segment commence à 0, même si son plan est ancré plus tard. */
  debut: number;
  fin: number;
  duree: number;
}

/** ⏮ revient au début de la scène si elle a dépassé ce seuil, sinon à la scène précédente. */
export const SEUIL_DEBUT_SCENE_S = 3;

/**
 * Les segments d'un chapitre. `duree` est celle de la piste : le dernier segment
 * s'y arrête. Sans plan, aucun segment (un chapitre à une scène sans séquence).
 */
export function segmentsDuChapitre(plans: readonly SceneShotCue[], duree: number): Segment[] {
  const ordonnes = [...plans].sort((a, b) => a.time - b.time);
  return ordonnes.map((plan, index) => {
    const debut = index === 0 ? 0 : plan.time;
    const suivant = ordonnes[index + 1];
    const fin = suivant ? suivant.time : Math.max(duree, debut);
    return { index, sceneId: plan.sceneId, debut, fin, duree: Math.max(0, fin - debut) };
  });
}

/** Le segment lu à l'instant `t` : le dernier commencé. -1 s'il n'y en a aucun. */
export function segmentA(segments: readonly Segment[], t: number): number {
  let courant = -1;
  for (const segment of segments) {
    if (segment.debut <= t) courant = segment.index;
    else break;
  }
  return segments.length === 0 ? -1 : Math.max(0, courant);
}

/** Où va ⏮ : le début de la scène en cours si on l'a écoutée plus de 3 s, sinon celui de la précédente. */
export function cibleScenePrecedente(segments: readonly Segment[], t: number): number {
  const index = segmentA(segments, t);
  const courant = segments[index];
  if (!courant) return 0;
  if (t - courant.debut > SEUIL_DEBUT_SCENE_S) return courant.debut;
  return segments[index - 1]?.debut ?? 0;
}

/** Où va ⏭ : le début de la scène suivante, ou null à la dernière (c'est alors la fin du chapitre). */
export function cibleSceneSuivante(segments: readonly Segment[], t: number): number | null {
  const index = segmentA(segments, t);
  return segments[index + 1]?.debut ?? null;
}
